'use client';

import {
  ForwardRefRenderFunction,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { forwardRef } from 'react';
import { RadioButton, RadioButtonProps } from '../RadioButton';

export type RadioButtonFieldProps = {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;
  asRadix?: boolean;
} & RadioButtonProps;

const RadioButtonFieldRenderFunction: ForwardRefRenderFunction<
  HTMLInputElement | null,
  RadioButtonFieldProps
> = ({ label, note, error, asRadix, ...attrs }, forwardedRef) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => inputRef.current,
  );

  useEffect(() => {
    if (asRadix) {
      return;
    }

    inputRef.current?.setCustomValidity(error ?? '');
  }, [asRadix, error]);

  return (
    <div className="space-y-1">
      <label className="flex cursor-pointer gap-2 text-marine-high-emphasis">
        <RadioButton {...attrs} ref={inputRef} asRadix={asRadix} />

        {(label || note) && (
          <div>
            {label && <div className="font-semibold">{label}</div>}
            {note && <div className="text-sm">{note}</div>}
          </div>
        )}
      </label>

      {error && <div className="text-sm text-error-100">{error}</div>}
    </div>
  );
};

export const RadioButtonField = forwardRef<
  HTMLInputElement | null,
  RadioButtonFieldProps
>(RadioButtonFieldRenderFunction);
